const verdictColors = {
    'Pass': 'bg-green-100 text-green-800',
    'Fail': 'bg-red-100 text-red-800',
};

const VerdictBadge = ({ verdict }) => {
    if (!verdict) return null;
    const color = verdictColors[verdict] || 'bg-gray-100 text-gray-700';
    return <span className={`inline-block px-2.5 py-1 text-xs font-semibold rounded-full ${color}`}>{verdict}</span>;
};

const fmt = (v) => {
    if (v === null || v === undefined) return '—';
    return typeof v === 'number' ? v.toLocaleString('en-GH', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : v;
};

const pct = (v) => {
    if (v === null || v === undefined || v === 0) return '—';
    return `${(v * 100).toFixed(1)}%`;
};

function DTISection({ dti }) {
    if (!dti || (!dti.totalVerifiedIncome && !dti.finalDTI)) {
        return (
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-y border-gray-200 rounded-t-lg">
                    <h3 className="text-sm font-semibold text-gray-600 uppercase tracking-wide">DTI Reconciliation</h3>
                </div>
                <div className="px-4 py-6 text-center text-sm text-gray-400">
                    Click "Compute DTI & Score" to generate DTI analysis from bank and MoMo data.
                </div>
            </div>
        );
    }

    return (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-y border-gray-200 rounded-t-lg">
                <h3 className="text-sm font-semibold text-gray-600 uppercase tracking-wide">DTI Reconciliation</h3>
                <VerdictBadge verdict={dti.dtiVerdict} />
            </div>

            {/* Income Side */}
            <div className="px-4 pt-3 pb-1">
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Income</span>
            </div>
            <div className="divide-y divide-gray-100">
                <div className="flex items-center justify-between px-4 py-2.5">
                    <span className="text-sm text-gray-600">Verified bank income (avg salary)</span>
                    <span className="text-sm font-medium text-gray-900">GHS {fmt(dti.verifiedBankIncome)}</span>
                </div>
                <div className="flex items-center justify-between px-4 py-2.5">
                    <span className="text-sm text-gray-600">
                        MoMo adjusted income
                        {dti.momoIncomeIncluded && <span className="ml-1 text-xs text-green-600">(included)</span>}
                        {!dti.momoIncomeIncluded && <span className="ml-1 text-xs text-gray-400">(excluded)</span>}
                    </span>
                    <span className={`text-sm font-medium ${dti.momoIncomeIncluded ? 'text-gray-900' : 'text-gray-400'}`}>
                        GHS {fmt(dti.momoAdjustedIncome)}
                    </span>
                </div>
                <div className="flex items-center justify-between px-4 py-2.5 bg-blue-50/50">
                    <span className="text-sm font-medium text-gray-700">Total verified income</span>
                    <span className="text-sm font-bold text-[#1e3a6e]">GHS {fmt(dti.totalVerifiedIncome)}</span>
                </div>
            </div>

            {/* Debt Side — Initial */}
            <div className="px-4 pt-4 pb-1">
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Debt — Initial (Declared)</span>
            </div>
            <div className="divide-y divide-gray-100">
                <div className="flex items-center justify-between px-4 py-2.5">
                    <span className="text-sm text-gray-600">Declared existing debt</span>
                    <span className="text-sm font-medium text-gray-900">GHS {fmt(dti.declaredDebt)}</span>
                </div>
                <div className="flex items-center justify-between px-4 py-2.5">
                    <span className="text-sm text-gray-600">Indicative new loan payment (PMT)</span>
                    <span className="text-sm font-medium text-gray-900">GHS {fmt(dti.indicativePayment)}</span>
                </div>
                <div className="flex items-center justify-between px-4 py-2.5 bg-yellow-50/50">
                    <span className="text-sm font-medium text-gray-700">Initial DTI</span>
                    <span className="text-sm font-bold text-gray-900">{pct(dti.initialDTI)}</span>
                </div>
            </div>

            {/* Debt Side — Final (with undisclosed) */}
            <div className="px-4 pt-4 pb-1">
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Debt — Final (Including Undisclosed)</span>
            </div>
            <div className="divide-y divide-gray-100">
                <div className="flex items-center justify-between px-4 py-2.5">
                    <span className="text-sm text-gray-600">Undisclosed bank loan payments</span>
                    <span className={`text-sm font-medium ${dti.undisclosedBankLoans > 0 ? 'text-red-600' : 'text-gray-900'}`}>
                        GHS {fmt(dti.undisclosedBankLoans)}
                    </span>
                </div>
                <div className="flex items-center justify-between px-4 py-2.5">
                    <span className="text-sm text-gray-600">Undisclosed MoMo loan payments</span>
                    <span className={`text-sm font-medium ${dti.undisclosedMomoLoans > 0 ? 'text-red-600' : 'text-gray-900'}`}>
                        GHS {fmt(dti.undisclosedMomoLoans)}
                    </span>
                </div>
                <div className="flex items-center justify-between px-4 py-2.5 bg-gray-50">
                    <span className="text-sm font-medium text-gray-700">Total debt</span>
                    <span className="text-sm font-bold text-gray-900">GHS {fmt(dti.totalDebt)}</span>
                </div>
                <div className={`flex items-center justify-between px-4 py-3 ${dti.finalDTI > 0.4 ? 'bg-red-50' : 'bg-green-50'}`}>
                    <span className="text-sm font-semibold text-gray-700">Final DTI</span>
                    <div className="flex items-center gap-2">
                        <span className={`text-sm font-bold ${dti.finalDTI > 0.4 ? 'text-red-700' : 'text-green-700'}`}>
                            {pct(dti.finalDTI)}
                        </span>
                        <VerdictBadge verdict={dti.dtiVerdict} />
                    </div>
                </div>
            </div>
        </div>
    );
}

export default DTISection;
